/**
 * WorkSpace Handbook — practical launch guides for founders.
 * Shares the ARTICLE content-block shape so we can reuse the existing
 * Article renderer without building a new CMS.
 */

export const HANDBOOK = [
  {
    slug: "getting-started-with-workspace",
    title: "Getting Started with WorkSpace",
    category: "Getting Started",
    read: "5 min read",
    date: "Launch",
    hero: "/product-shots/command-center-real.png",
    excerpt: "Set up your Workspace, add business context, and go from empty workspace to real work in under fifteen minutes.",
    content: [
      { type: "p", text: "This guide walks you from account creation to a workspace that already knows enough about your business to be useful. It should take about ten to fifteen minutes." },
      { type: "h2", text: "1. Workspace setup" },
      { type: "p", text: "After you create your account you'll go through a short setup that asks about your business, what you sell, who you sell to, your stage and what your first strategic goal is. Do not rush this — this context becomes the shared memory every module uses to help you." },
      { type: "ul", items: [
        "Business name — the actual name you use with customers. If you're a freelancer, that's fine — use your own name.",
        "What you do — one line the Board can ground its perspectives in.",
        "Stage — pre-launch, launched, growing, etc.",
        "One Primary Strategic Goal — you can refine or replace this later.",
      ]},
      { type: "h2", text: "2. Add real context, not placeholder data" },
      { type: "p", text: "Even a couple of real deals, a real contact and a real task will teach WorkSpace more than dozens of fake rows. The Board and Business Briefing draw on this reality." },
      { type: "h2", text: "3. Open Command Center" },
      { type: "p", text: "Command Center is your operational home. It surfaces today's priorities, overdue work, follow-ups and pipeline health. It's designed to help you act — not to make decisions." },
      { type: "h2", text: "4. Meet your Leadership Team" },
      { type: "p", text: "Open Board once your workspace has some real data. Read what the CEO, CGO, CMO, CFO, COO, CTO and CHRO have to say. If they ask for context, provide it — a Board that guesses is less useful than a Board that asks." },
      { type: "h2", text: "5. Delegate — don't just ask" },
      { type: "p", text: "Business Assistant can create tasks, contacts, companies and deals from natural language. Instead of typing forms, tell it what you want to happen." },
      { type: "quote", text: "You don't rise to the level of your ambition. You fall to the level of your systems." },
    ],
  },
  {
    slug: "running-your-day-from-command-center",
    title: "Running Your Day from Command Center",
    category: "Command Center",
    read: "4 min read",
    date: "Launch",
    hero: "/product-shots/command-center-real.png",
    excerpt: "Command Center is where you see what needs attention, prioritise, and act. It's not where you strategise.",
    content: [
      { type: "p", text: "Command Center = RUN. Board = THINK. Keeping this distinction clear is the single biggest way to get value out of WorkSpace quickly." },
      { type: "h2", text: "What Command Center is for" },
      { type: "ul", items: [
        "Seeing today's priorities in one glance",
        "Catching overdue work before it snowballs",
        "Spotting deals that need follow-up",
        "Checking your upcoming commitments",
        "Watching goal progress and pipeline pulse",
      ]},
      { type: "h2", text: "What Command Center is NOT for" },
      { type: "ul", items: [
        "Strategic brainstorming — open Board",
        "Long-term planning — Board + Goals",
        "Detailed CRM work — open Sales",
        "Building custom trackers — open Business Data",
      ]},
      { type: "h2", text: "A simple morning routine" },
      { type: "p", text: "Open Command Center. Read the Business Briefing. Clear or reschedule anything overdue. Send one follow-up. Look at your upcoming commitments. That's your day framed in under five minutes." },
    ],
  },
  {
    slug: "getting-the-most-from-your-leadership-team",
    title: "Getting the Most from Your Leadership Team",
    category: "Board",
    read: "6 min read",
    date: "Launch",
    hero: "/product-shots/board-real.png",
    excerpt: "The Board is where you think. Ask, challenge, provide context, consider, adopt, execute.",
    content: [
      { type: "p", text: "Board is your virtual leadership team. It examines your workspace from specialist perspectives so you don't make every business decision alone." },
      { type: "h2", text: "Who's on the Board" },
      { type: "ul", items: [
        "CEO — Strategy: are we solving the right problem?",
        "CGO — Growth: where does growth actually come from?",
        "CMO — Marketing: how do we tell the story?",
        "CFO — Finance: does the maths work?",
        "COO — Operations: can we execute at this pace?",
        "CTO — Technology: is the stack helping or holding us back?",
        "CHRO — People: who and how do we hire?",
        "Chief of Staff — turns adopted decisions into concrete execution plans.",
      ]},
      { type: "h2", text: "How to use it well" },
      { type: "p", text: "Follow a simple loop: Ask → Challenge → Provide Context → Consider → Adopt → Execute." },
      { type: "ul", items: [
        "Ask specific strategic questions grounded in your data.",
        "Challenge assumptions — the Board is stronger when disagreed with.",
        "Provide context when a member asks for it. Guessing is worse than asking.",
        "Consider the perspectives — not every recommendation should be adopted.",
        "Adopt the decisions that matter, and let the Chief of Staff draft execution.",
      ]},
      { type: "quote", text: "A Board that asks you for context is worth more than a Board that guesses." },
    ],
  },
  {
    slug: "delegate-dont-just-ask",
    title: "Don't Just Ask. Delegate.",
    category: "Business Assistant",
    read: "5 min read",
    date: "Launch",
    hero: "/product-shots/ask.png",
    excerpt: "Business Assistant isn't just a chatbot. Learn to give effective instructions that get real work done.",
    content: [
      { type: "p", text: "The most valuable thing about Business Assistant isn't that it answers questions. It's that it can do work in your workspace. Learn to give it effective instructions." },
      { type: "h2", text: "Give context, not just commands" },
      { type: "p", text: "Instead of: 'Create task.'" },
      { type: "p", text: "Prefer: 'Create a high-priority task to follow up with ABC tomorrow at 11 AM, related to the ABC growth-plan deal.'" },
      { type: "h2", text: "Ask for outcomes" },
      { type: "p", text: "Tell it what you want to have happened, not just what to do. 'Make sure I don't drop the Falcon follow-up this week' is a stronger instruction than 'remind me about Falcon.'" },
      { type: "h2", text: "Use follow-up instructions" },
      { type: "p", text: "You don't have to say everything in one message. Adjust, refine, and follow up — the assistant keeps context within the conversation." },
      { type: "h2", text: "Examples of things you can delegate today" },
      { type: "ul", items: [
        "Create a task, contact, company or deal with all the details in one sentence.",
        "Draft a follow-up email to a specific contact using their actual context.",
        "Summarise the state of your pipeline and highlight what needs attention.",
        "Ask for a review of your week's work.",
      ]},
    ],
  },
  {
    slug: "creating-business-trackers",
    title: "Creating Business Trackers",
    category: "Business Data",
    read: "4 min read",
    date: "Launch",
    hero: "/product-shots/data-real.png",
    excerpt: "When to use Business Data instead of CRM, Projects or Work — with examples.",
    content: [
      { type: "p", text: "Business Data lets you spin up custom trackers for records that don't fit CRM or Projects. But it's not a general database — it exists to complement the core engines, not replace them." },
      { type: "h2", text: "Use CRM for" },
      { type: "ul", items: [
        "Companies you sell to",
        "Contacts at those companies",
        "Sales opportunities and pipeline",
      ]},
      { type: "h2", text: "Use Projects for" },
      { type: "ul", items: [
        "Time-bound delivery with milestones and completion",
      ]},
      { type: "h2", text: "Use Work for" },
      { type: "ul", items: [
        "Individual tasks with due dates",
      ]},
      { type: "h2", text: "Use Business Data for" },
      { type: "ul", items: [
        "Inventory items",
        "Vendors and suppliers",
        "Candidates and hiring pipelines",
        "Marketing campaigns",
        "Channel partners and resellers",
        "Company assets and equipment",
        "Certifications, licences and warranties",
        "Anything persistent and structured that doesn't fit above",
      ]},
      { type: "h2", text: "How the Tracker Builder works" },
      { type: "p", text: "Describe what you want to track in a sentence. WorkSpace proposes a schema — you review, edit and approve. If the request duplicates CRM/Projects/Work/Goals it will politely redirect you." },
    ],
  },
  {
    slug: "install-workspace",
    title: "Install WorkSpace on Your Devices",
    category: "Install",
    read: "3 min read",
    date: "Launch",
    hero: "/product-shots/command-center-real.png",
    excerpt: "Install WorkSpace as an app on Android, iPhone/iPad, Windows and Mac for app-like access from your home screen.",
    content: [
      { type: "p", text: "WorkSpace is a Progressive Web App. That means you can use it in your browser or install it for app-like access — without downloading a large traditional application." },
      { type: "h2", text: "Android" },
      { type: "ul", items: [
        "Open WorkSpace in Chrome.",
        "Tap the browser menu (three dots).",
        "Select 'Install app' or 'Add to Home screen' when available.",
        "Launch WorkSpace from your device home screen.",
      ]},
      { type: "h2", text: "iPhone / iPad" },
      { type: "ul", items: [
        "Open WorkSpace in Safari.",
        "Tap the Share button.",
        "Choose 'Add to Home Screen'.",
        "Launch WorkSpace from your home screen.",
      ]},
      { type: "h2", text: "Windows / Mac" },
      { type: "ul", items: [
        "Open WorkSpace in a supported browser (Chrome, Edge, Arc).",
        "Look for an 'Install' option in the address bar or browser menu.",
        "Once installed, WorkSpace opens like a native app.",
      ]},
      { type: "h2", text: "Why install it?" },
      { type: "ul", items: [
        "Quick launch from your home screen or dock.",
        "App-like window without browser chrome.",
        "Same underlying WorkSpace — no data duplication.",
        "Updates arrive automatically without manual downloads.",
      ]},
    ],
  },
];

export function getHandbookArticle(slug) {
  return HANDBOOK.find((a) => a.slug === slug);
}
